import { Client } from '@microsoft/microsoft-graph-client';
import { EmailData, EmailAttachment } from '@shared/types/email';
import { API_BASE_URL } from '@shared/utils/constants';
import { addinLogger } from '../utils/addinLogger';
import { getAccessToken, isSsoAvailable } from './authService';

interface GraphRecipient {
  emailAddress?: { name?: string; address?: string };
}
interface GraphHeader {
  name: string;
  value: string;
}
interface GraphAttachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline?: boolean;
}

/** API response envelope for GET /email/{messageId} */
interface GetEmailApiResponse {
  success?: boolean;
  data?: EmailData;
  error?: { message?: string };
}

class GraphService {
  private client: Client | null = null;

  private async getClient(): Promise<Client> {
    if (this.client) {
      return this.client;
    }

    if (!isSsoAvailable()) {
      throw new Error(
        'Sign-in is not available. Office SSO is only supported in Outlook with a work or school account. See docs/ENTRA_SSO_SETUP.md.'
      );
    }

    addinLogger.debug('GraphService: getting token via Office SSO');
    const token = await getAccessToken();
    addinLogger.debug('GraphService: token received, init client');
    this.client = Client.init({
      authProvider: (done) => {
        done(null, token);
      },
    });
    return this.client;
  }

  /**
   * Load email via backend API (On-Behalf-Of flow). The backend exchanges the add-in SSO token
   * for a Graph token and returns the message, so we avoid "Invalid audience" from calling Graph directly.
   */
  async getEmail(emailId: string): Promise<EmailData> {
    addinLogger.debug('GraphService.getEmail', `id=${emailId?.slice(0, 20)}... (via API OBO)`);
    if (!isSsoAvailable()) {
      throw new Error(
        'Sign-in is not available. Office SSO is only supported in Outlook with a work or school account.'
      );
    }
    const token = await getAccessToken();
    const url = `${API_BASE_URL}/email/${encodeURIComponent(emailId)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    const json = (await response.json()) as GetEmailApiResponse;
    if (!response.ok) {
      const msg = json.error?.message || response.statusText || 'Failed to load email';
      addinLogger.error('GraphService.getEmail: API request failed', msg);
      addinLogger.debug('GraphService.getEmail: error details', `status=${response.status} body=${JSON.stringify(json)}`);
      throw new Error(msg);
    }
    if (!json.success || !json.data) {
      const msg = json.error?.message || 'Invalid response from email API';
      addinLogger.error('GraphService.getEmail: API response invalid', msg);
      throw new Error(msg);
    }
    return json.data;
  }

  private async getAttachments(emailId: string): Promise<EmailAttachment[]> {
    const client = await this.getClient();
    const response = await client.api(`/me/messages/${emailId}/attachments`).get();

    return (response.value || []).map((att: GraphAttachment) => ({
      id: att.id,
      name: att.name,
      contentType: att.contentType,
      size: att.size,
      isInline: att.isInline || false,
    }));
  }

  async getAttachmentContent(emailId: string, attachmentId: string): Promise<string> {
    const client = await this.getClient();
    const attachment = await client
      .api(`/me/messages/${emailId}/attachments/${attachmentId}`)
      .get();

    return attachment.contentBytes || '';
  }
}

export const graphService = new GraphService();





